import { CancellationToken, Cancelled } from 'infra/cancellation'
import { PublishedLegend } from 'legends/PublishedLegend'
import { computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { createTransformer } from 'mobx-utils'
import {
  combineLatest,
  merge,
  NEVER,
  Observable,
  of,
  firstValueFrom,
} from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  find,
  map,
  scan,
  share,
  skip,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { fromStream } from 'utils/mobx-util'
import { shareBehavior, shareLatest, withCancellation } from 'utils/rx-util'
import { AnswerDTO } from '../dtos/questions'
import Job from '../Job'
import { QuestionViewModel } from '../questions/component-registry'
import QuestionListViewModel from '../questions/QuestionListViewModel'
import { mergeAnswers } from '../questions/util'
import { QuestionsQuerySession } from '../QuestionStore'
import { AnswersChanged } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepContainer, StepFactory, StepViewModel } from './OutlineViewModel'
import { QuestionsPort } from './ports'

export default class IntakeQuestionsViewModel extends DraftStateSlice {
  /**
   * Transforms the question to a step container.
   *
   * @private
   */
  private readonly transformQuestionToStepContainer = createTransformer(
    (question: QuestionViewModel) =>
      new QuestionStepViewModel(this.createStep, this.isReadOnly, question),
  )

  /**
   * When creating a new Draft, we can preselect answers.
   * These always gets set BEFORE the `job$` observable emits, since we can only
   * preselect on new drafts.
   *
   * @private
   */
  private preselectedAnswers: AnswerDTO[] = []

  /**
   * Pieces of infra that are derived from the Job.
   *
   * @private
   */
  private readonly context$ = this.job$.pipe(
    switchMap((job) => {
      // Creates a fresh context for the job.
      const createContext = (): IntakeQuestionsContext => ({
        job,
        list: this.adapter.createQuestionListViewModel(job),
        session: this.adapter.createQuestionsQuerySession(job),
      })

      return merge(
        // Create a context for the current state of the job.
        of(createContext()),
        // Once we have a context, we also subscribe to Legend changes. If the
        // Legend is cleared, emit the no-op context. If a Legend is then
        // selected, we create a new context.
        this.legendChange$.pipe(
          map((legend) => (legend === null ? NO_CONTEXT : createContext())),
        ),
      )
    }),
    distinctUntilChanged(),
    shareLatest(),
  )

  /**
   * The questions VM.
   */
  private readonly questionList$ = this.context$.pipe(map((x) => x.list))

  /**
   * Stream of answers.
   * Emits latest answers on subscribe.
   *
   * @private
   */
  private readonly answers$ = this.job$.pipe(
    // When we have a Job instance...
    switchMap((job) =>
      // If we have any preselected answers, consume them. Otherwise
      // use what is on the Job.
      merge(
        of(this.maybeConsumePreselectedAnswers() ?? job.answers),
        // Then emit whenever the answers have changed.
        this.questionList$.pipe(switchMap((list) => list.answersChanged$)),
      ).pipe(
        // This lets us keep answers that are not relevant anymore but
        // in a way that won't alter the behavior of answering questions.
        // If we change paths (or even legends!), then change back, we
        // will remember the answers.
        scan(mergeAnswers, []),
      ),
    ),
    // Anytime someone subscribes, emit the latest answers we have.
    shareLatest(),
  )

  /**
   * Changes observable that emits whenever the answers change or when
   * a Legend was updated (this will queue a save of the answers
   * we had collected already).
   *
   * Example:
   *  1. Select Legend1 (Legend change)
   *  2. Answers a few questions (Answers change)
   *  3. Select Legend2 (Legend change)
   *  4. Select Legend 1 (Legend change)
   *  5. The answers from Step 2 are emitted and causes the Job to be saved
   *     with those answers (because changing the Legend clears the answers
   *     on the server).
   */
  readonly changes$ = merge(
    // Skip the first emission since that will be from a load.
    this.answers$.pipe(skip(1)),
    // When the Legend changes, take the first emission from the answers stream.
    this.legendChange$.pipe(
      filter((legend) => legend !== null),
      switchMap(() => this.answers$.pipe(take(1))),
    ),
  ).pipe(
    map(
      (answers): AnswersChanged => ({
        answers,
        type: 'AnswersChanged',
      }),
    ),
    share(),
  )

  /**
   * If..
   *  1. The context changed
   *  2. The Legend changed
   *  3. The answers changed
   * Then query for the updated question list.
   *
   * The observable emits a `boolean` of whether the questionnaire is completed.
   *
   * @private
   */
  private readonly query$ = this.context$.pipe(
    switchMap((ctx) => combineLatest([of(ctx), this.answers$])),
    switchMap(([ctx, answers]) =>
      // Any time we have a set of answers to provide to the server,
      // send a query. If we have no context, cancel the previous query if any.
      ctx === NO_CONTEXT
        ? NEVER
        : withCancellation((ct) => this.queryQuestions(ctx, answers, ct)),
    ),
    this.whileActive(),
    share(),
  )

  /**
   * Derivation that tracks whether we are running the first query for the
   * current context. This is used to show a loader but only while the first
   * query is running.
   *
   * @private
   */
  private readonly isRunningFirstQuery$ = this.context$.pipe(
    switchMap((ctx) =>
      ctx === NO_CONTEXT
        ? of(false)
        : merge(
            // Immediately when we get a new context, signal that the first query
            // is running.
            of(true),
            // When the next query completes, signal that the first query is
            // no longer running
            this.query$.pipe(
              take(1),
              map(() => false),
            ),
          ),
    ),
    distinctUntilChanged(),
    shareBehavior(false),
  )

  /**
   * Whether all questions have been completed.
   */
  readonly allQuestionsCompleted$ = combineLatest([
    this.context$,
    this.query$,
    this.isRunningFirstQuery$,
  ]).pipe(
    map(
      ([ctx, completed, runningFirst]) =>
        ctx !== NO_CONTEXT && !runningFirst && completed,
    ),
    distinctUntilChanged(),
    shareBehavior<boolean>(false),
  )

  /**
   * Streams as observables.
   * @private
   */
  private readonly $ = {
    questionList: fromStream(QuestionListViewModel.none, this.questionList$),
    isRunningFirstQuery: fromStream(false, this.isRunningFirstQuery$),
    allQuestionsCompleted: fromStream(false, this.allQuestionsCompleted$),
  }

  /**
   * Constructor.
   */
  constructor(
    isActive$: Observable<boolean>,
    private createStep: StepFactory,
    private adapter: QuestionsPort,
    private isReadOnly: () => boolean,
    private job$: Observable<Job>,
    private legendChange$: Observable<PublishedLegend | null>,
  ) {
    super(isActive$)
    makeObservable(this)
    this.answers$.subscribe()
    this.query$.subscribe()
  }

  /**
   * Step containers for this slice.
   */
  @computed
  get stepContainers(): QuestionStepViewModel[] {
    // Don't return any questions while we are running the first query
    // for a context.
    if (this.isRunningFirstQuery) {
      return []
    }

    return this.questionList.questions.map(
      this.transformQuestionToStepContainer,
    )
  }

  /**
   * Gets the Questions.
   */
  @computed
  get questionList() {
    return this.$.questionList()
  }

  /**
   * MobX getter for the observable of the same name.
   */
  @computed
  get isRunningFirstQuery() {
    return this.$.isRunningFirstQuery()
  }

  /**
   * Whether we are currently querying.
   */
  @computed
  get querying() {
    return this.queryQuestions.pending
  }

  /**
   * Whether the questionnaire is completed.
   */
  @computed
  get completed() {
    return this.$.allQuestionsCompleted()
  }

  /**
   * Prepares this slice.
   *
   * @protected
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    this.preselectedAnswers = []
    if (context.type === 'EditDraft') {
      // Wait for the first questions query to complete
      // if we have a job. This prevents flickering of the
      // main view and the outline.
      await this.waitForNextQuery(ct)
    } else {
      this.preselectedAnswers = context.prefilledDraft.answers
    }
  }

  /**
   * Waits for the next query to complete (if one is queued).
   *
   * @param ct If cancelled, returns immediately.
   */
  waitForNextQuery = (ct = CancellationToken.default) =>
    firstValueFrom(
      this.isRunningFirstQuery$.pipe(
        find((v) => !v),
        takeUntil(ct.asObservable()),
      ),
      { defaultValue: false },
    )

  /**
   * Queries questions
   */
  private queryQuestions = task.resolved(
    async (
      context: IntakeQuestionsContext,
      answers: AnswerDTO[],
      ct: CancellationToken,
    ): Promise<boolean> => {
      try {
        const result = await context.session.fetchQuestions(answers)
        ct.throwIfCancelled()
        context.list.reconcile(result.questions)
        return result.done
      } catch (err) {
        // TODO: Retries? What do we actually want to do?
        if (!(err instanceof Cancelled)) {
          console.error('Failed to query questions', err)
        }

        return false
      }
    },
  )

  /**
   * If there are preselected answers, consumes them by returning them and
   * clearing the backing field.
   *
   * @private
   */
  private maybeConsumePreselectedAnswers() {
    if (this.preselectedAnswers.length === 0) {
      return null
    }

    const answers = this.preselectedAnswers
    this.preselectedAnswers = []
    return answers
  }
}

/**
 * Context object used for scoping state to a specific job.
 */
interface IntakeQuestionsContext {
  job: Job
  session: QuestionsQuerySession
  list: QuestionListViewModel
}

/**
 * Default context.
 */
const NO_CONTEXT: IntakeQuestionsContext = {
  job: Job.none,
  list: QuestionListViewModel.none,
  session: QuestionsQuerySession.none,
}

/**
 * Question step.
 */
export class QuestionStepViewModel implements StepContainer {
  readonly step: StepViewModel

  constructor(
    createStep: StepFactory,
    private _isDisabled: () => boolean,
    public question: QuestionViewModel,
  ) {
    makeObservable(this)
    this.step = createStep({
      stepId: `question_${question.id.toLowerCase()}`,
      isShortCircuited: () => question.type === 'BREAKER',
      isValid: () => question.validate().type === 'Success',
      outlineItemDisplay: () => ({
        title: question.question,
        value: question.isAnswered ? question.toTextAnswer() : undefined,
        shortValue: question.isAnswered
          ? question.toTextAnswer(100)
          : undefined,
        visual: question.isAnswered
          ? { type: 'QuestionIcon', questionType: question.type }
          : undefined,
        description: question.description,
      }),
    })
  }

  @computed
  get disabled() {
    return this._isDisabled()
  }
}
